/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { PortableText } from '@portabletext/react';
import React from 'react';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { getComponents } from '../../portableText';
import { BackgroundColorTheme } from '../../templates/Page';
import { RawPortableText } from '../../types/types';
import { clsx, replaceNewLinesWithBr, slugify } from '../../utils/utils';
import ButtonLink from './ButtonLink';
import * as styles from './ModuleLayout.module.scss';

export interface ModuleLayoutProps {
  id?: string;
  title?: string;
  text?: string | RawPortableText;
  buttons?: Array<ButtonLinkType>;
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  textContainerClassName?: string;
  contentDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse' | 'centered';
  currentModuleBgColor?: BackgroundColorTheme;
  childrenWithoutHorizontalPadding?: boolean;
  childrenClassName?: string;
  headingVariant?: 'h1' | 'h2';
}

export function getModuleId(id?: string, title?: string) {
  return id || (title && slugify(title));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default React.forwardRef<any, ModuleLayoutProps>(function ModuleLayout(
  {
    id,
    title,
    text,
    buttons,
    children,
    className,
    contentClassName,
    textContainerClassName,
    contentDirection,
    currentModuleBgColor,
    childrenWithoutHorizontalPadding,
    childrenClassName,
    headingVariant,
  },
  ref,
): React.ReactElement {
  return (
    <section
      ref={ref}
      className={clsx(
        className,
        styles.moduleContainer,
        childrenWithoutHorizontalPadding &&
          contentDirection !== 'row' &&
          contentDirection !== 'row-reverse' &&
          styles.childrenWithoutHorizontalPadding,
      )}
      id={getModuleId(id, title)}
      data-theme={currentModuleBgColor}
    >
      <div
        className={clsx(
          styles.contentContainer,
          contentDirection === 'row' && styles.rowLayout,
          contentDirection === 'row-reverse' && styles.rowReverseLayout,
          contentDirection === 'column-reverse' && styles.columnReverseLayout,
          contentDirection === 'centered' && styles.centeredLayout,
          contentClassName,
        )}
      >
        {(title || text || (buttons && buttons.length > 0)) && (
          <div className={clsx(textContainerClassName, styles.textWrapper)}>
            <div className={styles.textContainer}>
              {title &&
                (headingVariant === 'h1' ? (
                  <h1 className={styles.title}>{title}</h1>
                ) : (
                  <h2 className={styles.title}>{title}</h2>
                ))}
              {text &&
                (typeof text === 'string' ? (
                  <p className={styles.text}>{replaceNewLinesWithBr(text)}</p>
                ) : (
                  <div className={styles.text}>
                    <PortableText value={text} components={getComponents()} />
                  </div>
                ))}
              {buttons && buttons.length > 0 && (
                <div className={styles.buttonsContainer}>
                  {buttons.map((button, index) => (
                    <ButtonLink button={button} key={index}></ButtonLink>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {children && contentDirection !== 'centered' && (
          <div className={clsx(childrenClassName, styles.children)}>{children}</div>
        )}
      </div>
    </section>
  );
});
